/**
 * Words on the "Simple" home page (layout key "simple"): one headline, one Upload bar, then one
 * section each for the phone app, Claude / ChatGPT and Premiere Pro.
 *
 * Same honesty rule as every marketing page: each claim matches what the product does today
 * (ET_BEHAVIOR_SPEC.md, the connector guide, the app listings). No YouTube-link promise: imports
 * from YouTube fail on the server's network, so the link box names Drive / Dropbox / a video link.
 */
import { LEGAL_FOOTER_OWNER, LEGAL_TRADEMARKS } from "../../../legal/legalConstants";

export const SIMPLE_NAV = {
  how: "Try it",
  app: "Phone app",
  ai: "Claude & ChatGPT",
  premiere: "Premiere",
  pricing: "Pricing",
  library: "My Library",
  signIn: "Sign in",
  getApp: "Get the app",
};

/**
 * The headlines the owner can switch between on the live site (site-layout switcher). Each is
 * line 1, then line 2 with its last words in cherry.
 */
export const SIMPLE_HEADLINES: Record<"becomes" | "is" | "cut" | "cherries", { l1: string; l2: string; hot: string }> = {
  becomes: { l1: "Every sentence in your video", l2: "becomes a ", hot: "clip." },
  is: { l1: "Every sentence", l2: "is a ", hot: "clip." },
  cut: { l1: "Your video is already cut.", l2: "", hot: "Just cherry-pick." },
  cherries: { l1: "Every story has its cherries.", l2: "", hot: "Pick yours." },
};

export const SIMPLE_HERO = {
  /** Under the headline: the whole product as three numbered steps, like a playlist. */
  steps: ["Upload a video", "Cherry-pick the lines you want", "Download them as one video"],
  drop: "Drop a video here",
  dropOr: "or",
  dropping: "Let go to upload",
  upload: "Upload a video",
  pasteToggle: "Or paste a link",
  pastePlaceholder: "Paste a YouTube, Drive or Dropbox link",
  /** Under the link box: the one setting a Drive / Dropbox link needs (tested live 2026-10-25). */
  pasteHint: "Google Drive or Dropbox? Set sharing to “Anyone with the link” first.",
  pasteGo: "Import",
  pasteBusy: "Checking…",
  language: "Language",
  ticks: ["Your first hour is free", "Nothing to install", "Sign in with Google"],
  back: "Already uploaded?",
  backLink: "Open My Library →",
};

/** Above the bar on layouts W / Y / Z, which lead with the phone app. */
export const UPLOAD_HERE_LABEL = "On a computer? Upload right here, or get the free app.";

/** The language menu: every language the transcriber knows (constants/languages.ts). */
export const SIMPLE_LANG = {
  auto: "Auto-detect (any language)",
  mixed: "Mixed: English, Hebrew, Yiddish",
  popular: "Most used",
  all: (n: number) => `All ${n} languages`,
};

export const SIMPLE_PROGRESS = {
  uploading: "Uploading…",
  next: "Next, every sentence becomes a clip.",
  finishing: "Almost there…",
  done: "Uploaded. Opening My Library…",
  batch: (n: number, of: number) => `Video ${n} of ${of}`,
  eta: (s: number) => (s >= 90 ? `About ${Math.round(s / 60)} minutes left` : `About ${Math.max(1, Math.round(s))} seconds left`),
};

export const SIMPLE_SIGNIN = {
  fileReady: (name: string) => `“${name}” is ready. Sign in and it starts straight away.`,
  linkReady: "Your link is ready. Sign in and it starts straight away.",
};

export const SIMPLE_DEMO = {
  tag: "Try it",
  title: "The real editor, right here",
  lede: "A real film, already cut into clips. Search what was said, tick lines, press play. Signed in? Pick one of your own videos from the list under it.",
};

/** The three ways to use CherryClipz, under the demo. */
export const SIMPLE_WAYS = {
  title: "One spreadsheet. Three ways to cut.",
  worksWith: "Works with",
  items: [
    {
      id: "self",
      h: "Pick them yourself",
      p: "Upload on the website, or use the free phone app. Tick the lines you want and download them as one video.",
      cta: "Upload a video",
    },
    {
      id: "ai",
      h: "Let Claude or ChatGPT pick",
      p: "Connect CherryClipz once, then ask in plain words. Their picks land in your editor, in order.",
      cta: "How to connect →",
    },
    {
      id: "pro",
      h: "Edit in Premiere Pro",
      p: "For editors: the panel puts the same clip list inside Premiere. Tick a line and it lands on your timeline.",
      cta: "Get the panel →",
    },
  ],
};

/** The partner marks in the "Works with" row (logos in home3/brandIcons.ts). */
export const SIMPLE_PARTNERS: { key: "pr" | "claude" | "openai" | "gemini" | "appstore" | "gplay" | "gdrive" | "dropbox"; name: string }[] = [
  { key: "pr", name: "Premiere Pro" },
  { key: "claude", name: "Claude" },
  { key: "openai", name: "ChatGPT" },
  { key: "gemini", name: "Gemini" },
  { key: "appstore", name: "iPhone" },
  { key: "gplay", name: "Android" },
  { key: "gdrive", name: "Google Drive" },
  { key: "dropbox", name: "Dropbox" },
];

export const SIMPLE_APP = {
  eyebrow: "Phone app",
  title: "Your clips, on your phone.",
  body: "Film or pick a video on your phone and CherryClipz turns every sentence into a clip. Tick the ones you want with your thumb and export them as one video.",
  free: "Free on iPhone and Android.",
  qr: "Scan with your phone's camera to get the app.",
  open: "More about the app →",
};

export const SIMPLE_AI = {
  eyebrow: "Claude & ChatGPT",
  title: "Or just ask your AI.",
  body: "Connect CherryClipz to Claude or ChatGPT once. Then ask for the clips you want, in plain words, and watch them land in your editor in order.",
  steps: ["Copy our address", "Add it in Claude or ChatGPT", "Ask for the clips you want"],
  example: "“Find every line about art and put them in order.”",
  sound: "Play with sound",
  cta: "How to connect →",
};

export const SIMPLE_PREMIERE = {
  eyebrow: "Premiere Pro",
  title: "Pick a line. It lands on your timeline.",
  body: "The CherryClipz panel puts the same clip list inside Premiere Pro, on Mac and Windows. Tick the lines you want and they land on your timeline, in order. Free to download.",
  download: "Download the panel",
  downloading: "Starting…",
  downloadFailed: "Couldn't start the download. Try again, or email us and we'll send it.",
  signInFirst: "Sign in and the Premiere panel download starts straight away.",
  more: "See how it works →",
  shotAlt: "The CherryClipz panel inside Premiere Pro",
};

export const SIMPLE_DEMOS = {
  title: "Watch it work",
  sub: "Three short films, with sound.",
  items: [
    { id: "demo", h: "The full demo", p: "Upload to export, start to finish." },
    { id: "tour", h: "One sheet, everywhere", p: "The website, the phone, Premiere and your AI." },
    { id: "spot", h: "Stop cutting", p: "A 24-second CherryClipz spot." },
  ],
  close: "Close",
};

export const SIMPLE_PRICING = {
  title: "Your first hour is free.",
  body: "When you need more, plans start at $10 a month.",
  cta: "See plans",
};

export const SIMPLE_FOOTER = {
  // Names the company behind the brand: OpenAI's plugin review checks the publisher on our pages.
  copyright: LEGAL_FOOTER_OWNER,
  trademarks: LEGAL_TRADEMARKS,
  links: [
    { label: "Pricing", to: "/pricing/" },
    { label: "Phone app", to: "/app" },
    { label: "Premiere panel", to: "/plugin" },
    { label: "Claude & ChatGPT", to: "/ai" },
    { label: "Contact", to: "/contact/" },
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
  ],
};
